import React, { useMemo, useState } from "react";
import { TOKENS, Icon, ICONS, inputClass } from "../common/DashboardUI";

// ── Permission grouping (for the selection matrix) ──────────────────────────
// Ordered: the first matching category wins. Keeps the ~70 permissions readable
// by clustering them by the module they belong to. The backend doesn't provide
// groups, so this infers one from the permission name.
const GROUPS = [
  { key: "dashboard", label: "Dashboard", match: (n) => n.includes("dashboard") },
  { key: "system_users", label: "System Users", match: (n) => n.includes("system_user") },
  { key: "roles", label: "Roles & Permissions", match: (n) => n.includes("role") || n.includes("permission") },
  { key: "members", label: "Members", match: (n) => n.includes("member") && !n.includes("club_member") },
  { key: "clubs", label: "Clubs", match: (n) => n.includes("club") },
  { key: "coaches", label: "Coaches", match: (n) => n.includes("coach") },
  { key: "facilities", label: "Facilities", match: (n) => n.includes("facilit") },
  { key: "bookings", label: "Bookings & Entry", match: (n) => n.includes("booking") || n.includes("entry") },
  { key: "payments", label: "Payments", match: (n) => n.includes("payment") || n.includes("refund") },
  { key: "fees", label: "Fees", match: (n) => n.includes("fee") },
  { key: "id_cards", label: "ID Cards", match: (n) => n.includes("card") },
  { key: "attendance", label: "Attendance", match: (n) => n.includes("attendance") || n.includes("scan") || n.includes("qr") },
  { key: "reports", label: "Reports", match: (n) => n.includes("report") },
  { key: "other", label: "Other", match: () => true },
];

const groupOf = (name) => GROUPS.find((g) => g.match(name)) || GROUPS[GROUPS.length - 1];

// ── Permission selection matrix ─────────────────────────────────────────────
// Friendly chooser: collapsible module sections (collapsed by default so the
// ~70 permissions stay scannable), each showing a live "selected/total" badge
// and a one-tap group toggle. A master toolbar offers search + Select-all /
// Clear-all + a running count. Whole rows are clickable with a clear selected
// state. `selected` is a Set of permission ids.
export function PermissionSelector({ permissions, selected, onChange, disabled }) {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set()); // group keys collapsed

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? permissions.filter(
          (p) => p.name.toLowerCase().includes(q) || String(p.description || "").toLowerCase().includes(q)
        )
      : permissions;
    const map = new Map(GROUPS.map((g) => [g.key, { ...g, items: [] }]));
    filtered.forEach((p) => map.get(groupOf(p.name).key).items.push(p));
    return [...map.values()].filter((g) => g.items.length > 0);
  }, [permissions, query]);

  const searching = query.trim().length > 0;
  // While searching every matching group is forced open so results are visible.
  const isOpen = (key) => searching || !collapsed.has(key);

  const toggleOpen = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggle = (id) => {
    if (disabled) return;
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(next);
  };

  const toggleGroup = (items, allSelected) => {
    if (disabled) return;
    const next = new Set(selected);
    items.forEach((p) => (allSelected ? next.delete(p.id) : next.add(p.id)));
    onChange(next);
  };

  const selectAll = () => !disabled && onChange(new Set(permissions.map((p) => p.id)));
  const clearAll = () => !disabled && onChange(new Set());

  const total = permissions.length;
  const count = selected.size;

  return (
    <div className="min-w-0">
      {/* Toolbar: search on its own row, running count + master actions grouped
          below so they wrap together as a unit instead of interleaving with
          the search box on narrow containers. */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 mb-3">
        <div className="relative flex-1 min-w-0">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
            <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
          </span>
          <input
            className={`${inputClass} pl-9`}
            placeholder="Search permissions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className="text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0"
            style={{ backgroundColor: TOKENS.LIGHT, color: TOKENS.PRIMARY }}
          >
            {count} / {total} selected
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={selectAll}
              disabled={disabled || count === total}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              style={{ color: TOKENS.PRIMARY }}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={disabled || count === 0}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-gray-500 whitespace-nowrap"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        {grouped.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No permissions match.</p>
        ) : (
          grouped.map((g) => {
            const selectedInGroup = g.items.filter((p) => selected.has(p.id)).length;
            const allSelected = selectedInGroup === g.items.length;
            const open = isOpen(g.key);
            return (
              <div key={g.key} className="rounded-xl border border-gray-200 overflow-hidden">
                {/* Group header */}
                <div className="flex items-center bg-gray-50">
                  <button
                    type="button"
                    onClick={() => toggleOpen(g.key)}
                    className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2.5 text-left"
                  >
                    <Icon
                      path={ICONS.chevron}
                      className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${open ? "" : "-rotate-90"}`}
                      width={2}
                    />
                    <span className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                      {g.label}
                    </span>
                    <span
                      className="text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                      style={
                        selectedInGroup
                          ? { backgroundColor: TOKENS.LIGHT, color: TOKENS.PRIMARY }
                          : { backgroundColor: "#e5e7eb", color: "#6b7280" }
                      }
                    >
                      {selectedInGroup}/{g.items.length}
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => toggleGroup(g.items, allSelected)}
                    className="flex-shrink-0 whitespace-nowrap px-3 py-2.5 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ color: TOKENS.PRIMARY }}
                  >
                    {allSelected ? "Clear" : "Select all"}
                  </button>
                </div>

                {/* Group body — auto-fill/minmax sizes columns off the actual
                    container width (this card may sit beside a sidebar or in a
                    modal), so it reflows correctly instead of relying on
                    viewport-based breakpoints that assume full-width. */}
                {open && (
                  <div className="p-3 grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2.5 border-t border-gray-100">
                    {g.items.map((p) => {
                      const checked = selected.has(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          disabled={disabled}
                          onClick={() => toggle(p.id)}
                          title={p.name}
                          className={`min-w-0 flex items-start gap-2.5 text-left px-3 py-2.5 rounded-lg border transition disabled:cursor-not-allowed ${
                            checked
                              ? "border-blue-300 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <span
                            className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border ${
                              checked ? "border-blue-600" : "border-gray-300 bg-white"
                            }`}
                            style={checked ? { backgroundColor: TOKENS.PRIMARY } : undefined}
                          >
                            {checked && <Icon path={ICONS.check} className="w-3 h-3" stroke="#fff" width={3} />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm text-gray-700 leading-tight truncate">
                              {p.description || p.name}
                            </span>
                            <span className="block text-[11px] text-gray-400 font-mono truncate">{p.name}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
